<template>
  <div class="input-wrapper" :class="{ 'has-error': error }">
    <checkbox
      v-if="this.type === 'toggle'"
      class="mt-4 ml-5 mr-20"
      :readonly="readonly"
      :value="evaluateChecked(value)"
      @input="$emit('input', $event ? 1 : 0, name)"
    />

    <template v-else>
      <div
        class="wrap"
        v-if="
          editable && isExpandable && type !== 'number' && type !== 'toggle'
        "
      >
        <div class="pull-tab"></div>
        <textarea
          ref="input"
          :id="elementId"
          :placeholder="placeholder"
          :maxlength="maxlength"
          :value="value"
          :class="{
            focus: focused,
            readonly: readonly,
            disabled: disabled,
            editable: editable,
          }"
          class="input"
          :disabled="disabled"
          v-on:keyup.enter="save"
          @input="handleInput($event, name)"
          @blur="onBlur"
          @focus="onFocus"
          rows="1"
          :readonly="readonly"
          :name="name"
          :autocomplete="autocomplete"
        ></textarea>
        <label
          :for="labelPlaceholder"
          v-if="labelPlaceholder && !readonly"
        >{{ labelPlaceholder }}</label>
      </div>

      <template v-else>
        <input
          ref="input"
          :min="minValue"
          :maxlength="maxlength"
          :id="elementId"
          :placeholder="placeholder"
          :type="type"
          :value="value"
          :class="{
            focus: focused,
            readonly: readonly,
            disabled: disabled,
            editable: editable,
          }"
          class="input"
          :disabled="disabled"
          v-on:keyup.enter="save"
          @input="$emit('input', $event.target.value, name)"
          @blur="onBlur"
          :readonly="readonly"
          @focus="onFocus"
          :name="name"
          :autocomplete="autocomplete"
        />
        <label
          :for="labelPlaceholder"
          v-if="labelPlaceholder && !readonly"
        >{{ labelPlaceholder }}</label>
      </template>
    </template>

    <i
      @click="$refs['input'].focus()"
      v-if="
        !showPassword &&
          editable &&
          !disabled &&
          type !== 'number' &&
          type !== 'toggle'
      "
      style="font-size: 1.5em;"
      class="absolute top-0 right-0 p-1 edit-symbol"
    />

    <i
      @click="switchVisibillity"
      v-if="showPassword"
      type="password"
      class="absolute top-0 right-0 p-1 eye-symbol"
      :class="{ closed: this.toggleEye }"
    />

    <grafokett-button
      v-if="inlineSave && dataChanged"
      @click="save"
      class="absolute top-0 right-0 light small"
      >Save</grafokett-button
    >

    <span v-if="this.error && this.error !== ''" class="input-error">{{
      showError
    }}</span>
    <span v-if="maxlengthReached">Max {{ maxlength }} chars</span>
    <span v-if="maxlengthExceeded" class="input-error"
      >Max {{ maxlength }} chars</span
    >
  </div>
</template>

<script>
import GrafokettButton from "./Button";
import Checkbox from "../../components/forms/Checkbox";

export default {
  name: "grafokett-input",
  components: {
    GrafokettButton,
    Checkbox,
  },
  data() {
    return {
      isFocused: false,
      toggleEye: false,
      dataChanged: false,
      internalError: "",
    };
  },
  watch: {
    value(val) {
      if (!this.validateLength) {
        this.dataChanged = true;
      } else {
        if (val.length !== this.validateLength) {
          this.internalError = `Invalid length, a length of ${
            this.validateLength
          } is required`;
        } else {
          this.internalError = "";
          this.dataChanged = true;
        }
      }
    },
  },
  computed: {
    showError() {
      if (this.error) {
        return this.error;
      } else if (this.internalError !== "") {
        return this.internalError;
      }
      return "";
    },
    elementId() {
      return this.id ? this.id : this.labelPlaceholder;
    },
    focused() {
      if (
        this.value &&
        this.value !== "" &&
        !this.disabled &&
        this.labelPlaceholder &&
        this.labelPlaceholder !== ""
      ) {
        if (this.type === "number" && isNaN(this.value)) {
          return false;
        }
        return true;
      }

      if (this.value && this.value !== "" && this.editable) {
        if (this.type === "number" && isNaN(this.value)) {
          return false;
        }
        return true;
      }
      return this.isFocused;
    },
    maxlengthReached() {
      return (
        !this.readonly &&
        this.value &&
        this.maxlength &&
        this.value.length === this.maxlength
      );
    },
    maxlengthExceeded() {
      return (
        !this.readonly &&
        this.value &&
        this.maxlength &&
        this.value.length > this.maxlength
      );
    },
  },
  props: {
    id: {
      type: String,
      required: false,
    },
    value: {
      default: "",
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    editable: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: "text",
    },
    isInput: {
      type: Boolean,
      default: true,
    },
    labelPlaceholder: {
      type: String,
    },
    validateLength: {
      type: Number,
      default: 0,
    },
    placeholder: {
      type: String,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    maxlength: {
      type: Number,
      default: 2000,
    },
    name: {
      type: String,
    },
    error: {
      type: String,
    },
    showPassword: {
      type: Boolean,
      default: false,
    },
    inlineSave: {
      type: Boolean,
      default: false,
    },
    minValue: {
      type: Number,
      default: 0,
    },
    autocomplete: {
      type: String,
      default: "on",
    },
    isExpandable: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    evaluateChecked(v) {
      return v === true || v === "true" || v === 1 || v === "1" || v === "on";
    },
    save() {
      this.$emit("inlineSave");
      this.$emit("enterPressed");
      this.dataChanged = false;
    },
    onBlur(event) {
      this.isFocused = false;
      this.$emit("blur", event);
    },
    onFocus(event) {
      this.isFocused = true;
      this.$emit("focus", event);
    },
    switchVisibillity() {
      this.type = this.toggleEye ? "password" : "text";
      this.toggleEye = !this.toggleEye;
    },
    handleInput(event, name) {
      this.adjustHeight();
      this.$emit("input", event.target.value, name);
    },
    adjustHeight() {
      const textarea = this.$refs.input;
      textarea.style.height = "auto";
      textarea.style.minHeight = "1px";
      let minHeight = textarea.scrollHeight < 23 ? 23 : textarea.scrollHeight;
      textarea.style.height = minHeight + "px";
      textarea.style.minHeight = minHeight + "px";
    },
  },
  mounted() {
    this.adjustHeight();
  },
};
</script>

<style lang="postcss">
.input-wrapper {
  @apply relative inline-block w-full my-3;
  line-height: 30px;

  input,
  textarea {
    @apply w-full text-textcolor text-sm;
    background: none;
    border: transparent;
    border-bottom: rgba(theme("colors.textcolor"), 0.5) solid 2px;
    outline: none;

    &::placeholder {
      /* Chrome, Firefox, Opera, Safari 10.1+ */
      color: rgba(theme("colors.textcolor"), 0.25);
      opacity: 1; /* Firefox */
    }

    &:-ms-input-placeholder {
      /* Internet Explorer 10-11 */
      color: rgba(theme("colors.textcolor"), 0.25);
    }

    &::-ms-input-placeholder {
      /* Microsoft Edge */
      color: rgba(theme("colors.textcolor"), 0.25);
    }
  }

  &.has-error {
    input,
    textarea,
    input.focus,
    textarea.focus {
      border-bottom: rgba(255, 0, 0, 0.5) solid 2px;
      margin-bottom: 0;
    }
  }

  input.readonly,
  textarea.readonly {
    border-bottom: rgba(theme("colors.textcolor"), 0.2) solid 1px;
  }

  input.focus,
  textarea.focus {
    @apply border-highlight;
  }

  input.editable,
  textarea.editable {
    @apply border-highlight;
    border-bottom-width: 2px;
  }

  input.disabled,
  textarea.disabled {
    border-bottom: rgba(theme("colors.textcolor"), 0.2) solid 2px;
    opacity: 0.4;
  }

  textarea {
    resize: vertical;
    font-family: inherit;
    position: relative;
    z-index: 1;
    overflow: hidden;
  }

  label {
    @apply absolute left-0 text-xs;
    top: 10%;
    transition: top 0.5s ease;
  }

  input.focus + label,
  textarea.focus + label,
  input.disabled + label,
  textarea.disabled + label {
    top: -1.1em;
  }

  input.disabled + label,
  textarea.disabled + label {
    opacity: 0.9;
  }

  .input-error {
    @apply text-xs;
    color: red;
    line-height: 16px;
    font-weight: bold;
    background-color: #0b3b5c;
    display: block;
    padding-top: 1em;
    padding-bottom: 1em;
  }

  &.normalize-placeholder {
    label {
      @apply text-textcolor opacity-25 text-base;
    }

    input.focus + label,
    textarea.focus + label {
      top: 10%;
    }

    input.focus + label,
    textarea.focus + label {
      @apply opacity-0;
    }
  }

  &.inputb {
    input,
    textarea {
      color: theme("colors.textcolor")-dark;
      padding: 0.25rem 0.25rem;
      background-color: theme("colors.light-blue");
      border: 1px solid theme("colors.secondary");
      border-radius: 8px;

      &.focus {
        background-color: theme("colors.textcolor");
      }
    }
  }

  .wrap {
    position: relative;
  }
  .wrap:after {
    content: "";
    border-top: 10px solid #f59100;
    border-left: 10px solid transparent;
    border-right: 10px solid transparent;
    -webkit-transform: rotate(-45deg);
    z-index: 1;
    opacity: 0.5;
    position: absolute;
    right: -11px;
    bottom: 9px;
    pointer-events: none;
  }
  .pull-tab {
    height: 0px;
    width: 0px;
    border-top: 10px solid #f59100;
    border-left: 10px solid transparent;
    border-right: 10px solid transparent;
    -webkit-transform: rotate(-45deg);
    position: absolute;
    bottom: 9px;
    right: -11px;
    pointer-events: none;
    z-index: 2;
  }
}
</style>
